import React, { useEffect, useState } from 'react';
import { Check, X, AlertCircle, Info } from 'lucide-react';
import './Toast.css';

export interface ToastMessage {
  id: string;
  message?: string;
  customContent?: React.ReactNode;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

interface ToastProps {
  toasts: ToastMessage[];
  onRemoveToast: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({ toasts, onRemoveToast }) => {
  const getIcon = (type: ToastMessage['type']) => {
    switch (type) {
      case 'success':
        return <Check size={16} />;
      case 'error':
        return <X size={16} />;
      case 'warning':
        return <AlertCircle size={16} />;
      case 'info':
      default:
        return <Info size={16} />;
    }
  };

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onRemove={onRemoveToast}
          icon={getIcon(toast.type)}
        />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: ToastMessage;
  onRemove: (id: string) => void;
  icon: React.ReactNode;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove, icon }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show animation
    const showTimer = setTimeout(() => setIsVisible(true), 50);
    
    // Auto-remove timer
    const removeTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onRemove(toast.id), 300); // Wait for exit animation
    }, toast.duration || 3000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(removeTimer);
    };
  }, [toast.id, toast.duration, onRemove]);

  const handleClick = () => {
    setIsVisible(false);
    setTimeout(() => onRemove(toast.id), 300);
  };

  return (
    <div
      className={`toast toast--${toast.type} ${isVisible ? 'toast--visible' : ''}`}
      onClick={handleClick}
    >
      <div className="toast__icon">
        {icon}
      </div>
      <div className="toast__message">
        {toast.customContent || toast.message}
      </div>
      <button className="toast__close" onClick={handleClick}>
        <X size={14} />
      </button>
    </div>
  );
};
